import { App } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type File from '../../../interfaces/File';
import { FileService } from '../../../services/fileApiService';

const fileService = new FileService();
const SEARCH_PAGE_SIZE = 50;

export interface UseFolderPickerGlobalSearchParams {
  /** True at virtual root (Files / Received / Favorites picker). */
  enabled: boolean;
  keyword: string;
  /** When true, API exact match; when false, like/wildcard (GlobalSearchField default). */
  exactMatch?: boolean;
}

/**
 * Root-level global search for folder pickers via `/myfiles/search` (same as GlobalSearchField).
 * When `enabled` is false or keyword empty, results clear so the root picker / prior list returns.
 * Keyword debouncing lives in FolderPickerToolbar; this hook fetches as soon as keyword settles.
 */
export function useFolderPickerGlobalSearch({
  enabled,
  keyword,
  exactMatch = false,
}: UseFolderPickerGlobalSearchParams) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [results, setResults] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);

  const trimmed = keyword.trim();
  const isActive = enabled && trimmed.length > 0;

  const runSearch = useCallback(
    (searchKeyword: string, matchExact: boolean) => {
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      const generation = ++generationRef.current;
      setIsLoading(true);

      void fileService
        .searchFiles(
          {
            keyword: searchKeyword,
            includeFolder: true,
            includeTags: true,
            offset: 0,
            count: SEARCH_PAGE_SIZE,
            orderColumn: 0,
            exactMatch: matchExact,
          },
          { signal: abortController.signal },
        )
        .then((response) => {
          if (generation !== generationRef.current) return;
          setResults(response?.myFiles ?? []);
        })
        .catch((error) => {
          if (generation !== generationRef.current) return;
          if (
            error?.name === 'CanceledError' ||
            error?.code === 'ERR_CANCELED' ||
            abortController.signal.aborted
          ) {
            return;
          }
          notification.error({
            title: t('errors.genericError'),
          });
          setResults([]);
        })
        .finally(() => {
          if (generation === generationRef.current) {
            setIsLoading(false);
          }
        });
    },
    [notification, t],
  );

  useEffect(() => {
    if (!isActive) {
      abortRef.current?.abort();
      generationRef.current += 1;
      setResults([]);
      setIsLoading(false);
      return;
    }

    // Mark loading immediately so forms never treat pending search as empty folder.
    setIsLoading(true);
    runSearch(trimmed, exactMatch);
  }, [isActive, trimmed, exactMatch, runSearch]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return {
    globalSearchResults: results,
    isGlobalSearching: isLoading,
    isGlobalSearchActive: isActive,
  };
}
